import { SUPPORT_LABEL, type ProviderInfo } from './types';

/**
 * Mirror of the server's provider catalog (EPN.PayrollReview.Api ProviderCatalog), used ONLY by the in-browser
 * mock gateway and to give a stored source system a readable name. The real screens always render the provider
 * list the server returns in capabilities.providers; nothing here decides what is "supported".
 *
 * EPN claims native support only for formats it owns and tests (the Escochex package and the EPN Standard
 * template). Every other provider is imported through guided column mapping, and its own export format is
 * explicitly "not yet verified".
 */
function notVerified(id: string, displayName: string): ProviderInfo {
  return {
    id, displayName, supportLevel: 'NotVerified', supportLabel: SUPPORT_LABEL.notVerified,
    sourceSystem: `GenericMapped:${id}`, acceptedFileTypes: ['.xlsx', '.csv'],
    description: `EPN has not verified ${displayName}'s export format. You can still upload a spreadsheet or CSV export and map its columns yourself; nothing is assumed.`,
    templateKind: null,
  };
}

export const PROVIDER_CATALOG: ProviderInfo[] = [
  {
    id: 'EscochexPayroll', displayName: 'Escochex Payroll', supportLevel: 'NativeTemplate', supportLabel: SUPPORT_LABEL.native,
    sourceSystem: 'EscochexPayroll', acceptedFileTypes: ['.zip'],
    description: 'The Escochex Payroll History Import Package (a .zip of CSV files). EPN reads this format natively.',
    templateKind: 'template',
  },
  {
    id: 'EpnStandardTemplate', displayName: 'EPN Standard Payroll History Template (Excel)', supportLevel: 'NativeTemplate',
    supportLabel: SUPPORT_LABEL.native, sourceSystem: 'EpnStandardTemplate', acceptedFileTypes: ['.xlsx'],
    description: 'Fill in the EPN Excel template with history from any system. EPN reads this format natively.',
    templateKind: 'standard-template',
  },
  {
    id: 'OtherGuided', displayName: 'Other payroll system, spreadsheet or CSV', supportLevel: 'GuidedMapping',
    supportLabel: SUPPORT_LABEL.guided, sourceSystem: 'GenericMapped:OtherGuided', acceptedFileTypes: ['.xlsx', '.csv'],
    description: 'Upload a payroll register export and tell EPN what each column means. EPN suggests, you confirm.',
    templateKind: null,
  },
  notVerified('QuickBooks', 'QuickBooks Payroll'),
  notVerified('ADP', 'ADP'),
  notVerified('Gusto', 'Gusto'),
  notVerified('Paycor', 'Paycor'),
  notVerified('Paycom', 'Paycom'),
  notVerified('Heartland', 'Heartland Payroll'),
];

export const MOCK_SOURCE_SYSTEMS: { id: string; displayName: string }[] = [
  { id: 'EscochexPayroll', displayName: 'Escochex Payroll' },
  { id: 'EpnStandardTemplate', displayName: 'EpnStandardTemplate' },
  { id: 'GenericMapped', displayName: 'GenericMapped' },
];

export function findProvider(id: string | null | undefined): ProviderInfo | undefined {
  return PROVIDER_CATALOG.find((p) => p.id.toLowerCase() === (id ?? '').toLowerCase());
}

/** The provider a stored source system belongs to ("GenericMapped:ADP" -> ADP; bare "GenericMapped" -> Other). */
export function providerForSourceSystem(sourceSystem: string): ProviderInfo | undefined {
  const colon = sourceSystem.indexOf(':');
  const adapter = colon < 0 ? sourceSystem : sourceSystem.slice(0, colon);
  if (adapter !== 'GenericMapped') return PROVIDER_CATALOG.find((p) => p.sourceSystem === adapter);
  return findProvider(colon < 0 ? 'OtherGuided' : sourceSystem.slice(colon + 1));
}
